import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, TrendingDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOURCES = ["", "Website", "GoHighLevel", "Upfirst AI", "Facebook", "Google", "TikTok", "Instagram", "Referral", "QR Code", "Repeat Customer", "Organic Search", "Walk-in", "Manual Entry"];
const RANGES = [{ v: 7, l: "7 days" }, { v: 30, l: "30 days" }, { v: 90, l: "90 days" }, { v: 365, l: "1 year" }];

export default function FunnelView({ token }) {
  const [data, setData] = useState(null);
  const [source, setSource] = useState("");
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams({ days });
    if (source) p.set("source", source);
    axios.get(`${API}/marketing/funnel?${p}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [token, source, days]);

  return (
    <div data-testid="funnel-view" className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-display font-bold text-white mr-auto">Sales Funnel {data && <span className="text-white/40 font-normal text-sm">· {data.source} · last {data.days} days</span>}</h3>
        <select data-testid="funnel-source-filter" value={source} onChange={(e) => setSource(e.target.value)} className="bg-black border border-white/15 rounded-md px-3 py-1.5 text-sm text-white/80">
          {SOURCES.map((s) => <option key={s} value={s}>{s || "All channels"}</option>)}
        </select>
        <div className="flex gap-1.5">
          {RANGES.map((r) => (
            <button key={r.v} onClick={() => setDays(r.v)} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${days === r.v ? "bg-[#F2B705] text-black" : "bg-white/5 border border-white/10 text-white/60"}`}>{r.l}</button>
          ))}
        </div>
      </div>

      {loading || !data ? (
        <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="animate-spin" size={18} /> Building funnel…</div>
      ) : (
        <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-6 space-y-1">
          {data.steps.map((s, i) => (
            <div key={s.label}>
              {i > 0 && s.drop_off > 0 && (
                <div className="flex items-center justify-center gap-1.5 text-red-400/70 text-xs py-1">
                  <TrendingDown size={12} /> lost {s.drop_off} here ({(100 - s.pct_of_prev).toFixed(1)}% drop-off)
                </div>
              )}
              <div data-testid={`funnel-step-${i}`} className="flex items-center gap-4">
                <div className="w-44 shrink-0 text-right">
                  <div className="text-white/85 text-sm font-semibold">{s.label}</div>
                  {i > 0 && <div className="text-white/40 text-xs">{s.pct_of_prev}% of previous</div>}
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="h-11 rounded-md bg-gradient-to-r from-[#F2B705] to-[#c99a04] flex items-center justify-center transition-all duration-500"
                    style={{ width: `${Math.max(s.pct_of_top, 6)}%`, opacity: s.count === 0 ? 0.25 : 1 }}>
                    <span className="text-black font-black text-sm px-2 whitespace-nowrap">{s.count.toLocaleString()}</span>
                  </div>
                </div>
                <div className="w-16 shrink-0 text-white/50 text-xs">{s.pct_of_top}%</div>
              </div>
            </div>
          ))}
          <p className="text-white/35 text-xs pt-4 text-center">Each bar's width is relative to Website Visits. Drop-off labels show exactly where customers bail.</p>
        </div>
      )}
    </div>
  );
}
